import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { put, select, takeEvery } from "redux-saga/effects";

import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { getFunnels } from "@app/store/deal";
import { handleError } from "@app/store/errors/actions";
import { getOrganization } from "@app/store/iam";

import {
  createDealLossReason,
  deleteDealLossReason,
  fetchDealLossReasons,
  updateDealLossReason,
} from "./actions";
import { getDealLossReasons } from "./selectors";

const messages = defineMessages({
  deleted: {
    id: "deals.setting.dealLostReasonModal.delete.success",
    defaultMessage: "Loss reason successfully deleted",
    description: "Success notification when a deal loss reason is deleted",
  },
});

// The payload's funnelId is ignored on purpose. The API call was always unscoped; filtering the
// response before storing it made the store mean "whatever the last caller asked for", which two
// concurrently open editors cannot share. Consumers that need a funnel's subset filter locally.
// (The narrowing never held anyway: onCreateDealLossReason refetches unscoped after every create.)
export function* onFetchDealLossReasons() {
  try {
    const org: Organization = yield select(getOrganization);
    const dealLossReasons: ListResponse<DealLossReason> = yield callApi(
      "fetchDealLossReasons",
      org.id
    );
    yield put(fetchDealLossReasons.success(dealLossReasons.data));
  } catch (error) {
    yield put(fetchDealLossReasons.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateDealLossReason({
  payload,
}: ReturnType<typeof createDealLossReason.request>) {
  const { callback, reason } = payload;
  try {
    const org: Organization = yield select(getOrganization);
    const lossReasons: DealLossReason[] = yield select(getDealLossReasons);
    const createdDealLossReason: DealLossReason = yield callApi("createDealLossReason", org.id, {
      ...reason,
      displayOrder: reason.displayOrder ?? lossReasons.length,
    });
    callback?.(createdDealLossReason);
    yield put(createDealLossReason.success(createdDealLossReason));
    yield put(fetchDealLossReasons.request());
  } catch (error) {
    yield put(createDealLossReason.failure());
    yield put(handleError({ error }));
  }
}

export function* onDeleteDealLossReason({
  payload: dealLossReasonId,
}: ReturnType<typeof deleteDealLossReason.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("deleteDealLossReason", org.id, dealLossReasonId);
    yield put(deleteDealLossReason.success(dealLossReasonId));
    notification.success({
      message: i18nService.formatMessage(messages.deleted, "Loss reason successfully deleted"),
    });
  } catch (error) {
    yield put(deleteDealLossReason.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateDealLossReason({
  payload: lossReason,
}: ReturnType<typeof updateDealLossReason.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const funnels: Funnel[] = yield select(getFunnels);
    const validFunnelIds = new Set(funnels.map(({ id }) => id));

    const updatedDealLossReason: DealLossReason = yield callApi("updateDealLossReason", org.id, {
      ...lossReason,
      // keep only valid funnel ids, which do exist now
      funnelId: lossReason.funnelId?.filter((id) => validFunnelIds.has(id)),
    });
    yield put(updateDealLossReason.success(updatedDealLossReason));
  } catch (error) {
    yield put(updateDealLossReason.failure());
    yield put(handleError({ error }));
  }
}

export function* dealLossReasonsSaga() {
  yield takeEvery(createDealLossReason.request, onCreateDealLossReason);
  yield takeEvery(updateDealLossReason.request, onUpdateDealLossReason);
  yield takeEvery(deleteDealLossReason.request, onDeleteDealLossReason);
  yield takeEvery(fetchDealLossReasons.request, onFetchDealLossReasons);
}
